if (!customElements.get('real-results')) {
  customElements.define(
    'real-results',
    class RealResults extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.ejectNestedSections();

        this.featuredPanel = this.querySelector('[data-panel="featured"]');
        this.fullPanel = this.querySelector('[data-panel="full"]');
        this.panels = this.querySelector('.real-results__panels');
        this.mobileMedia = window.matchMedia('(max-width: 991px)');
        this.mobileMedia.addEventListener('change', () => {
          if (this.panels) this.panels.style.height = '';
          this.ejectNestedSections();
        });

        this.addEventListener('click', this.onClick.bind(this));
        this.setView(this.dataset.view || 'featured', { animate: false });
        this.setupJuxtaposeAspect();
      }

      /**
       * Juxtapose writes an inline height from the source image.
       * Mobile: lock 4:3. Desktop: fill the stretched column height.
       */
      setupJuxtaposeAspect() {
        const apply = () => {
          const frame = this.querySelector('.juxtapose');
          const frameWrap = this.querySelector('.before-after-frame');
          if (!frame) return;

          const isMobile = window.matchMedia('(max-width: 991px)').matches;
          const slider = frame.querySelector('.jx-slider');
          frame.style.width = '100%';

          if (isMobile) {
            const width = frame.getBoundingClientRect().width;
            if (!width) return;
            const height = Math.round(width * (3 / 4));
            frame.style.height = `${height}px`;
            if (slider) slider.style.height = `${height}px`;
            return;
          }

          const height = Math.round(frameWrap?.getBoundingClientRect().height || 0);
          if (!height) return;
          frame.style.height = `${height}px`;
          if (slider) slider.style.height = `${height}px`;
        };

        this._syncJuxtaposeAspect = apply;
        apply();
        window.addEventListener('resize', apply);
        [100, 400, 1000].forEach((delay) => window.setTimeout(apply, delay));

        const layout = this.querySelector('.real-results__layout');
        if (layout && 'ResizeObserver' in window) {
          this._juxtaposeResizeObserver = new ResizeObserver(apply);
          this._juxtaposeResizeObserver.observe(layout);
        }
      }

      /**
       * Broken HTML from metafield rich text can leave a flex wrapper open and
       * pull the following Shopify sections into this row. Move them back out.
       */
      ejectNestedSections() {
        const hostSection = this.closest('.shopify-section');
        if (!hostSection) return;

        const nestedSections = this.querySelectorAll('.shopify-section');
        if (!nestedSections.length) return;

        let anchor = hostSection;
        nestedSections.forEach((section) => {
          anchor.after(section);
          anchor = section;
        });
      }

      onClick(event) {
        const trigger = event.target.closest('[data-real-results-show]');
        if (!trigger || !this.contains(trigger)) return;

        event.preventDefault();
        this.setView(trigger.dataset.realResultsShow);
      }

      setView(view, { animate = true } = {}) {
        const nextView = view === 'full' ? 'full' : 'featured';
        if (this.dataset.view === nextView && animate) return;

        this.dataset.view = nextView;

        const outgoing = nextView === 'full' ? this.featuredPanel : this.fullPanel;
        const incoming = nextView === 'full' ? this.fullPanel : this.featuredPanel;
        const animateHeight = animate && this.mobileMedia?.matches && this.panels && incoming;

        if (animateHeight) {
          this.panels.style.height = `${this.panels.offsetHeight}px`;
        }

        if (!animate) {
          this.featuredPanel?.classList.toggle('is-active', nextView === 'featured');
          this.fullPanel?.classList.toggle('is-active', nextView === 'full');
          if (this.panels) this.panels.style.height = '';
          return;
        }

        if (!outgoing || !incoming) {
          if (this.panels) this.panels.style.height = '';
          return;
        }

        outgoing.classList.remove('is-active');
        outgoing.classList.add('is-leaving');

        incoming.classList.add('is-active');
        incoming.classList.remove('is-leaving');

        if (animateHeight) {
          this.animatePanelHeight(incoming);
        }

        const onFadeOut = (event) => {
          if (event.propertyName !== 'opacity') return;
          outgoing.classList.remove('is-leaving');
          outgoing.removeEventListener('transitionend', onFadeOut);
        };

        outgoing.addEventListener('transitionend', onFadeOut);
      }

      animatePanelHeight(incoming) {
        const endHeight = Math.ceil(incoming.getBoundingClientRect().height);
        const startHeight = this.panels.offsetHeight;

        if (this._onPanelHeightEnd) {
          this.panels.removeEventListener('transitionend', this._onPanelHeightEnd);
        }

        if (Math.abs(startHeight - endHeight) < 1) {
          this.panels.style.height = '';
          return;
        }

        this._panelHeightToken = (this._panelHeightToken || 0) + 1;
        const token = this._panelHeightToken;

        this._onPanelHeightEnd = (event) => {
          if (event.target !== this.panels || event.propertyName !== 'height') return;
          if (token !== this._panelHeightToken) return;
          this.panels.style.height = '';
          this.panels.removeEventListener('transitionend', this._onPanelHeightEnd);
        };

        this.panels.addEventListener('transitionend', this._onPanelHeightEnd);
        this.panels.style.height = `${endHeight}px`;
      }
    }
  );
}
